import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    CurrencyRupeeIcon,
    ChartBarIcon,
    BarsArrowDownIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    DocumentArrowDownIcon,
    // FIX: Added PlusCircleIcon to the imports
    PlusCircleIcon
} from '@heroicons/react/24/outline';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// Mock data for payroll overview
const mockPayrollSummary = {
    totalSalaries: 1250000, // in Rupees
    bonusesPaid: 150000,
    grossSalary: 1500000,
    netSalary: 1250000,
    taxDeductions: 250000,
};

// Mock data for monthly payroll trends
const mockMonthlyData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
    datasets: [
        {
            label: 'Gross Salary (in ₹)',
            data: [1500000, 1550000, 1600000, 1520000, 1650000, 1700000, 1750000],
            backgroundColor: 'rgba(75, 192, 192, 0.6)',
        },
        {
            label: 'Net Salary (in ₹)',
            data: [1250000, 1300000, 1350000, 1280000, 1400000, 1450000, 1500000],
            backgroundColor: 'rgba(255, 159, 64, 0.6)',
        },
    ],
};

const mockTransactions = [
    { id: 1, name: 'Jane Doe', month: 'July 2024', gross: 125000, net: 100000, status: 'Paid' },
    { id: 2, name: 'John Smith', month: 'July 2024', gross: 130000, net: 105000, status: 'Paid' },
    { id: 3, name: 'Emily White', month: 'July 2024', gross: 150000, net: 120000, status: 'Paid' },
    { id: 4, name: 'David Lee', month: 'July 2024', gross: 110000, net: 90000, status: 'Processing' },
    { id: 5, name: 'Sarah Chen', month: 'June 2024', gross: 125000, net: 100000, status: 'Paid' },
];

const PayrollOverview = () => {
    const [transactions, setTransactions] = useState(mockTransactions);
    const [sortConfig, setSortConfig] = useState({ key: 'month', direction: 'descending' });

    const sortedTransactions = useMemo(() => {
        let sortableTransactions = [...transactions];
        if (sortConfig.key) {
            sortableTransactions.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableTransactions;
    }, [transactions, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <BarsArrowDownIcon className="w-4 h-4 ml-1 text-gray-400" />;
        return sortConfig.direction === 'ascending' ? <ArrowUpIcon className="w-4 h-4 ml-1" /> : <ArrowDownIcon className="w-4 h-4 ml-1" />;
    };

    const formatCurrency = (amount) => {
        return `₹${new Intl.NumberFormat('en-IN').format(amount)}`;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-teal-100 to-lime-100 p-8">
            <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto"
            >
                {/* Header & Export Button */}
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 pb-4 border-b-2 border-green-400">
                    <h1 className="text-4xl font-extrabold text-green-800 tracking-tight mb-4 md:mb-0">
                        Payroll Overview
                    </h1>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-colors"
                    >
                        <DocumentArrowDownIcon className="h-5 w-5" />
                        <span className="font-semibold">Generate Report</span>
                    </motion.button>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Total Salaries Paid</h3>
                        <p className="text-4xl font-extrabold text-teal-600 mt-2">{formatCurrency(mockPayrollSummary.totalSalaries)}</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Gross Salary</h3>
                        <p className="text-4xl font-extrabold text-indigo-600 mt-2">{formatCurrency(mockPayrollSummary.grossSalary)}</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Net Salary</h3>
                        <p className="text-4xl font-extrabold text-lime-600 mt-2">{formatCurrency(mockPayrollSummary.netSalary)}</p>
                    </motion.div>
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                    >
                        <h3 className="text-lg font-bold text-gray-800">Tax Deductions</h3>
                        <p className="text-4xl font-extrabold text-red-600 mt-2">{formatCurrency(mockPayrollSummary.taxDeductions)}</p>
                    </motion.div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Monthly Payroll Trends Chart */}
                    <motion.div
                        className="lg:col-span-2 bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.6 }}
                    >
                        <div className="flex items-center space-x-2 mb-4 text-purple-600">
                            <ChartBarIcon className="h-6 w-6" />
                            <h3 className="text-lg font-bold">Monthly Payroll Trends</h3>
                        </div>
                        <div className="h-72">
                            <Bar
                                data={mockMonthlyData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: { legend: { position: 'top' } },
                                    scales: { y: { beginAtZero: true } }
                                }}
                            />
                        </div>
                    </motion.div>

                    {/* Placeholder for future options */}
                    <motion.div
                        className="bg-white rounded-3xl shadow-xl p-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.6 }}
                    >
                        <div className="flex items-center space-x-2 mb-4 text-purple-600">
                            <CurrencyRupeeIcon className="h-6 w-6" />
                            <h3 className="text-lg font-bold">Quick Actions</h3>
                        </div>
                        <div className="space-y-4">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-purple-100 text-purple-800 rounded-xl shadow-sm hover:bg-purple-200 transition-colors"
                            >
                                <PlusCircleIcon className="h-5 w-5" />
                                <span className="font-semibold">Process New Payroll</span>
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-purple-100 text-purple-800 rounded-xl shadow-sm hover:bg-purple-200 transition-colors"
                            >
                                <DocumentArrowDownIcon className="h-5 w-5" />
                                <span className="font-semibold">View Reports & Analytics</span>
                            </motion.button>
                        </div>
                    </motion.div>
                </div>

                {/* Recent Transactions Table */}
                <motion.div
                    className="mt-8 bg-white rounded-3xl shadow-xl overflow-hidden"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                >
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {['name', 'month', 'gross', 'net', 'status'].map(key => (
                                        <th
                                            key={key}
                                            onClick={() => requestSort(key)}
                                            className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center">
                                                {key.replace(/([A-Z])/g, ' $1').charAt(0).toUpperCase() + key.replace(/([A-Z])/g, ' $1').slice(1)}
                                                {getSortIcon(key)}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                <AnimatePresence initial={false}>
                                    {sortedTransactions.length > 0 ? (
                                        sortedTransactions.map(transaction => (
                                            <motion.tr
                                                key={transaction.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, x: -20, transition: { duration: 0.3 } }}
                                                transition={{ duration: 0.5, ease: 'easeOut' }}
                                                className="hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{transaction.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.month}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(transaction.gross)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(transaction.net)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        transaction.status === 'Paid' ? 'bg-green-100 text-green-800' :
                                                        'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                        {transaction.status}
                                                    </span>
                                                </td>
                                            </motion.tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No transactions found.</td>
                                        </tr>
                                    )}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default PayrollOverview;